import { ChordLib, analyzeChord, displayAccidentals, getNoteChromaticIndex, parseKey } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { TheoryLevel } from '../theory-copy/use-theory-copy.js'

/**
 * Copy for the cover-song "check this chord" marker: Joni's UX spec
 * (`docs/ux/2026-10-18-cover-song-chord-check-ux.md` §3 and the microcopy table). An annotation, never
 * an error: no copy here says "wrong", "error" or "invalid", and none claims an unmarked chord was
 * checked (Bowie's copy rule).
 */

/** The `checkHint` a chord node carries (chord-check spec §2), declared structurally. */
export interface ChordCheckHintLike {
  reason: 'chromatic' | 'unexplained-borrowing' | 'unresolved-applied-dominant'
  chordFunction: string
  keys: string[]
  inferred: boolean
  resolution?: { target: string; next: string }
  flagged: { root: string; variant: string }
}

const listOf = (items: string[]) =>
  items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

const orListOf = (items: string[]) =>
  items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`

export const chordCheckCopy = {
  heading: 'Worth a second listen',
  listen: 'Charts online are sometimes off by a chord. Play it against the recording.',
  hear: 'Hear it',
  keep: 'Keep this chord',
  /** Appended to the chip's accessible name (Joni §5). */
  chipSuffix: 'Worth a second listen.',
  alsoIn: (sections: string[]) => `Also in ${listOf(sections)}`,
  kept: (chord: string, places: number) => (places > 1 ? `Kept ${chord} in ${places} places.` : `Kept ${chord}.`),
  undo: 'Undo',
} as const

/** "C major" → "C major", "Bb minor" → "B♭ minor". */
const displayKey = (name: string) => {
  const [root, ...rest] = name.split(' ')
  return [displayAccidentals(root), ...rest].join(' ')
}

const scaleOf = (name: string): Scale | undefined => parseKey(name)

/** A minor key whose relative major (a minor third above) is also listed. */
const isRelativeMinorOfListed = (name: string, keys: string[]) => {
  const scale = scaleOf(name)
  if (!scale || scale.type !== 'minor') {
    return false
  }
  const minorTonic = getNoteChromaticIndex(scale.root)
  return keys.some(other => {
    const major = scaleOf(other)
    return major !== undefined && major.type !== 'minor' && (getNoteChromaticIndex(major.root) + 9) % 12 === minorTonic
  })
}

/**
 * The key the judgement was made in, worded for the musician (Joni §3): a stated key plainly; one
 * inferred key with "(our best guess at the key)"; tied keys with "(we couldn't tell which)". A
 * relative-key tie names the major key only — the two share a diatonic set.
 */
export const chordCheckKeyPhrase = (hint: ChordCheckHintLike): string => {
  const keys = hint.keys.filter(name => !isRelativeMinorOfListed(name, hint.keys)).map(displayKey)
  if (keys.length > 1) {
    return `${orListOf(keys)} (we couldn't tell which)`
  }
  const [key = ''] = keys
  return hint.inferred ? `${key} (our best guess at the key)` : key
}

/**
 * The quality-flip sentence (Joni §3, Bowie's commonest typo): only for a major triad or dominant
 * seventh whose minor counterpart is diatonic to the key — E for Em in C.
 */
const qualityFlip = (hint: ChordCheckHintLike, chordName: string): string => {
  const { root, variant } = hint.flagged
  if (variant !== '' && variant !== '7') {
    return ''
  }
  const minorName = `${root}m${variant}`
  const minor = ChordLib.fromName(minorName)
  const scale = hint.keys[0] ? scaleOf(hint.keys[0]) : undefined
  const analysis = minor && scale ? analyzeChord({ chord: minor, scale }) : null
  const isDiatonic = analysis !== null && analysis.borrowing === null && analysis.chordFunction !== 'chromatic'
  return isDiatonic ? ` Charts sometimes write ${chordName} where the record has ${displayAccidentals(minorName)}.` : ''
}

/** Why the chord is marked, keyed on `reason` and register by theory level (Joni §3 table). */
export const chordCheckWhy = (hint: ChordCheckHintLike, chordName: string, level: TheoryLevel): string => {
  const key = chordCheckKeyPhrase(hint)
  const fn = hint.chordFunction
  const target = displayAccidentals(hint.resolution?.target ?? '')
  const next = displayAccidentals(hint.resolution?.next ?? '')
  switch (hint.reason) {
    case 'chromatic':
      if (level === 'intermediate') {
        return `Chromatic in ${key}: not in the scale, and not set up by the chords around it.`
      }
      if (level === 'advanced') {
        return `Chromatic in ${key}; no applied or borrowed reading fits its neighbours.`
      }
      return `This chord is unusual in ${key}, and nothing around it leads to it.`
    case 'unexplained-borrowing':
      if (level === 'intermediate') {
        return `Borrowed (${fn}) in ${key}, with nothing around it setting it up.`
      }
      if (level === 'advanced') {
        return `${fn} in ${key}: modal mixture without preparation.`
      }
      return 'This chord is borrowed from another key, and nothing around it sets that up.'
    case 'unresolved-applied-dominant':
      if (level === 'intermediate') {
        return `${chordName} reads as ${fn} (leading to ${target}), but it goes to ${next} instead.`
      }
      if (level === 'advanced') {
        return `${fn} unresolved: ${chordName} → ${next}, not ${target}.`
      }
      return `${chordName} usually leads to ${target}, but here it goes to ${next}.${qualityFlip(hint, chordName)}`
  }
}
